'use client';

import { useState } from 'react';

interface BrochureRequestFormFrenchProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  brochureLanguage: string;
}

const BrochureRequestFormFrench = ({ isOpen, onClose }: BrochureRequestFormFrenchProps) => {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    brochureLanguage: 'french',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  if (!isOpen) return null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
      const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;
      
      if (!supabaseUrl) {
        throw new Error('URL Supabase non configurée');
      }

      // Prepare headers - Supabase Edge Functions require Authorization header for browser requests
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      // Authorization header is required for Supabase Edge Functions when called from browsers
      if (supabaseAnonKey) {
        headers['Authorization'] = `Bearer ${supabaseAnonKey}`;
      } else {
        console.warn('NEXT_PUBLIC_SUPABASE_ANON_KEY is not set. This may cause CORS/authentication errors.');
      }

      const url = `${supabaseUrl}/functions/v1/send-brochure-email`;
      console.log('Submitting to:', url);
      console.log('Headers:', headers);
      console.log('Body:', formData);

      const response = await fetch(url, {
        method: 'POST',
        mode: 'cors', // Explicitly set CORS mode
        credentials: 'omit', // Don't send cookies
        headers,
        body: JSON.stringify(formData),
      }).catch((fetchError) => {
        // Catch network errors (CORS, connection refused, etc.)
        console.error('Fetch error:', fetchError);
        console.error('Error details:', {
          name: fetchError.name,
          message: fetchError.message,
          stack: fetchError.stack
        });
        throw new Error(`Erreur réseau: ${fetchError.message}. Cela pourrait être un problème CORS ou le serveur est inaccessible.`);
      });

      console.log('Response status:', response.status, response.statusText);

      // Check if response is ok before trying to parse JSON
      if (!response.ok) {
        let errorMessage = 'Échec de l\'envoi de la demande de brochure';
        let errorDetails = null;
        
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
          errorDetails = errorData.details || null;
          
          // Log detailed error for debugging
          if (errorDetails) {
            console.error('Error details from server:', errorDetails);
          }
        } catch (parseError) {
          // If JSON parsing fails, use status text
          errorMessage = response.statusText || `Erreur serveur: ${response.status}`;
        }
        
        // Create a more descriptive error message
        const fullErrorMessage = errorDetails 
          ? `${errorMessage}${errorDetails.message ? `: ${errorDetails.message}` : ''}`
          : errorMessage;
        
        throw new Error(fullErrorMessage);
      }

      const data = await response.json();

      console.log('Brochure request submitted:', data);
      setSubmitSuccess(true);

      // Reset form after 2 seconds
      setTimeout(() => {
        setFormData({ firstName: '', lastName: '', company: '', email: '', brochureLanguage: 'french' });
        setSubmitSuccess(false);
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Error submitting brochure request:', err);
      // Better error messages for network errors
      if (err instanceof TypeError && (err.message.includes('fetch') || err.message.includes('Failed to fetch'))) {
        setErrorMessage('Erreur réseau: Impossible de se connecter au serveur. Cela pourrait être un problème CORS. Veuillez vérifier la console du navigateur pour plus de détails.');
      } else if (err instanceof Error && err.message.includes('Erreur réseau')) {
        setErrorMessage(err.message);
      } else {
        setErrorMessage(err instanceof Error ? err.message : 'Échec de l\'envoi de la demande. Veuillez réessayer.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={handleBackdropClick}
    >
      <div className="relative w-full max-w-md mx-4 bg-[#F4F1EA] rounded-sm shadow-2xl">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-[#0E3B3C] hover:text-[#A47C48] transition-colors"
          aria-label="Fermer"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Form Content */}
        <div className="p-8">
          <h2 className="text-2xl font-serif font-bold text-[#0E3B3C] mb-2" style={{ fontFamily: '"Playfair Display", serif' }}>
            Demander Notre Brochure
          </h2>
          <p className="text-sm text-[#0E3B3C]/70 mb-6">
            Veuillez remplir vos coordonnées pour recevoir notre brochure.
          </p>

          {submitSuccess ? (
            <div className="py-8 text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-lg font-medium text-[#0E3B3C]">
                Demande Soumise avec Succès !
              </p>
              <p className="text-sm text-[#0E3B3C]/70 mt-2">
                Nous vous enverrons la brochure sous peu.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {errorMessage && (
                <div className="p-3 bg-red-100 border border-red-300 text-red-700 text-sm rounded">
                  {errorMessage}
                </div>
              )}

              {/* First Name */}
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-[#0E3B3C] mb-1">
                  Prénom *
                </label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-[#0E3B3C]/20 bg-white text-[#0E3B3C] focus:outline-none focus:border-[#A47C48] transition-colors"
                />
              </div>

              {/* Last Name */}
              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-[#0E3B3C] mb-1">
                  Nom *
                </label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-[#0E3B3C]/20 bg-white text-[#0E3B3C] focus:outline-none focus:border-[#A47C48] transition-colors"
                />
              </div>

              {/* Company/Entity */}
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-[#0E3B3C] mb-1">
                  Société ou Entité *
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-[#0E3B3C]/20 bg-white text-[#0E3B3C] focus:outline-none focus:border-[#A47C48] transition-colors"
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-[#0E3B3C] mb-1">
                  Adresse E-mail *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-[#0E3B3C]/20 bg-white text-[#0E3B3C] focus:outline-none focus:border-[#A47C48] transition-colors"
                />
              </div>

              {/* Brochure Language */}
              <div>
                <label htmlFor="brochureLanguage" className="block text-sm font-medium text-[#0E3B3C] mb-1">
                  Langue de la Brochure *
                </label>
                <select
                  id="brochureLanguage"
                  name="brochureLanguage"
                  value={formData.brochureLanguage}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-[#0E3B3C]/20 bg-white text-[#0E3B3C] focus:outline-none focus:border-[#A47C48] transition-colors"
                >
                  <option value="english">Anglais</option>
                  <option value="french">Français</option>
                  <option value="arabic">Arabe</option>
                </select>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full mt-6 px-8 py-3 bg-[#0E3B3C] text-[#F4F1EA] border border-[#A47C48] font-medium tracking-wide transition-all duration-300 hover:bg-[#A47C48] hover:text-[#0E3B3C] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Envoi en cours...' : 'Demander la Brochure'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default BrochureRequestFormFrench;